import React, {
  useEffect, useId, useRef, useState,
} from 'react';

import { Check, Search } from 'lucide-react';

import { resolveEnter } from './autocompleteKeys';
import { Spinner } from './Spinner';
import { cn } from './utils';


/**
 * Autocomplete combobox per the DESIGN.md `autocomplete` spec (Pencil frame
 * D9CHZ "State · Search & select"): surface-1 search control with a leading
 * magnifier, a floating listbox of option rows, and an optional pinned footer
 * action.
 *
 * The primitive is presentation-only: it NEVER fetches or filters. The caller
 * owns search/debounce/fetch via `query`/`onQueryChange` and passes the rendered
 * `options`.
 *
 * Overlay choice: this uses a custom absolutely-positioned panel rather than
 * @radix-ui/react-popover (which IS a dependency, see ui/Popover.jsx). Radix
 * Popover installs a focus trap that steals focus from the text input, which
 * breaks the `aria-activedescendant` pattern this combobox relies on (focus must
 * stay on the input while the listbox is navigated). A custom panel keeps focus
 * on the input and lets us drive active-option highlighting from the keyboard.
 *
 * a11y: input is `role="combobox"` with `aria-expanded`/`aria-controls`/
 * `aria-activedescendant`/`aria-autocomplete="list"`/`aria-haspopup="listbox"`;
 * the panel is `role="listbox"`; rows are `role="option"` with `aria-selected`.
 */
const Autocomplete = React.forwardRef((props, ref) => {
  const {
    value = null,
    onChange,
    query = '',
    onQueryChange,
    options = [],
    getOptionLabel = (o) => o?.label ?? '',
    getOptionSubtitle,
    getOptionKey = (o, i) => o?.id ?? o?.value ?? getOptionLabel(o) ?? i,
    renderOption,
    loading = false,
    placeholder,
    emptyText = 'No results',
    footer,
    disabled = false,
    invalid = false,
    id,
    className,
    ...restProps
  } = props;

  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const containerRef = useRef(null);
  const inputRef = useRef(null);

  const setRef = (node) => {
    inputRef.current = node;
    if (typeof ref === 'function') {
      ref(node);
    }
    else if (ref) {
      ref.current = node;
    }
  };

  const reactId = useId();
  const listboxId = `${reactId}-listbox`;
  const optionId = (index) => `${reactId}-option-${index}`;

  // Reset the active row whenever the option set or query changes.
  useEffect(() => {
    setActiveIndex(options.length > 0 ? 0 : -1);
  }, [options, query]);

  // Close on click-outside.
  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const handleMouseDown = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [open]);

  const selectedKey = value != null ? getOptionKey(value, -1) : undefined;

  const isSelected = (option, index) => (
    value != null && getOptionKey(option, index) === selectedKey
  );

  const selectOption = (option) => {
    onChange?.(option);
    setOpen(false);
    inputRef.current?.focus();
  };

  const handleKeyDown = (event) => {
    if (disabled) {
      return;
    }

    switch (event.key) {
      case 'ArrowDown': {
        event.preventDefault();
        if (!open) {
          setOpen(true);
          return;
        }
        if (options.length === 0) {
          return;
        }
        setActiveIndex((prev) => (prev + 1) % options.length);
        break;
      }
      case 'ArrowUp': {
        event.preventDefault();
        if (!open) {
          setOpen(true);
          return;
        }
        if (options.length === 0) {
          return;
        }
        setActiveIndex((prev) => (prev <= 0 ? options.length - 1 : prev - 1));
        break;
      }
      case 'End': {
        if (open && options.length > 0) {
          event.preventDefault();
          setActiveIndex(options.length - 1);
        }
        break;
      }
      case 'Enter': {
        // While the dropdown is open, Enter must NEVER fall through to a form's
        // implicit submit (F2). Closed: stay presentation-neutral and let Enter
        // behave normally — the consuming form owns stray-submit guarding.
        const { action, index } = resolveEnter({ open, activeIndex, options });
        if (action === 'ignore') {
          break;
        }
        event.preventDefault();
        if (action === 'select' || action === 'selectOnly') {
          selectOption(options[index]);
        }
        break;
      }
      case 'Escape': {
        if (open) {
          event.preventDefault();
          setOpen(false);
          inputRef.current?.focus();
        }
        break;
      }
      case 'Home': {
        if (open && options.length > 0) {
          event.preventDefault();
          setActiveIndex(0);
        }
        break;
      }
      case 'Tab': {
        setOpen(false);
        break;
      }
      default:
        break;
    }
  };

  const activeOptionId = (open && activeIndex >= 0 && activeIndex < options.length)
    ? optionId(activeIndex)
    : undefined;

  const showEmpty = open && !loading && options.length === 0;
  const hasFooter = footer != null;
  const footerNode = typeof footer === 'function' ? footer() : footer;

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      <div
        className={cn(
          'flex h-10 w-full items-center gap-2 rounded-md bg-surface-1 px-3',
          'border border-hairline-strong',
          'focus-within:border-primary focus-within:ring-focus',
          invalid && 'border-danger focus-within:border-danger',
          disabled && 'cursor-not-allowed opacity-45',
        )}
      >
        <Search className="size-4 shrink-0 text-ink-subtle" />

        <input
          ref={setRef}
          id={id}
          type="text"
          value={query}
          disabled={disabled}
          aria-expanded={open}
          aria-activedescendant={activeOptionId}
          className={cn(
            'flex-1 border-0 bg-transparent text-[13.5px] text-ink',
            'placeholder:text-ink-subtle outline-none',
            'disabled:cursor-not-allowed',
          )}
          role="combobox"
          autoComplete="off"
          aria-haspopup="listbox"
          aria-autocomplete="list"
          aria-controls={listboxId}
          placeholder={placeholder}
          onKeyDown={handleKeyDown}
          onFocus={() => setOpen(true)}
          onChange={(event) => onQueryChange?.(event.target.value)}
          {...restProps}
        />

        {loading
          ? <Spinner size="sm" className="shrink-0" />
          : null}
      </div>

      {open
        ? (
          <div
            className={cn(
              'absolute left-0 right-0 top-full z-50 mt-1.5 overflow-hidden',
              'rounded-lg border border-hairline bg-surface-1 shadow-popover',
            )}
          >
            <ul
              id={listboxId}
              className="max-h-72 overflow-y-auto"
              role="listbox"
            >
              {loading
                ? (
                  <li className="flex items-center gap-2.5 px-3 py-2.5 text-[13px] text-ink-subtle">
                    <Spinner size="sm" />
                    Loading…
                  </li>
                )
                : null}

              {showEmpty
                ? (
                  <li className="px-3 py-2.5 text-[13px] text-ink-subtle">
                    {emptyText}
                  </li>
                )
                : null}

              {!loading && options.map((option, index) => {
                const active = index === activeIndex;
                const selected = isSelected(option, index);

                return (
                  <li
                     
                    id={optionId(index)}
                    aria-selected={selected}
                    key={getOptionKey(option, index)}
                    className={cn(
                      'flex cursor-pointer items-center gap-2.5 px-3 py-2.5',
                      active && 'bg-surface-2',
                    )}
                    role="option"
                    onMouseEnter={() => setActiveIndex(index)}
                    onMouseDown={(event) => {
                      // Prevent the input from blurring before the click lands.
                      event.preventDefault();
                      selectOption(option);
                    }}
                  >
                    {renderOption
                      ? renderOption(option, { active, selected })
                      : (
                        <>
                          <span className="size-[26px] shrink-0 rounded-full bg-surface-3" />

                          <span className="flex min-w-0 flex-col">
                            <span className="truncate text-[13px] text-ink">
                              {getOptionLabel(option)}
                            </span>

                            {getOptionSubtitle
                              ? (
                                <span className="truncate text-xs text-ink-subtle">
                                  {getOptionSubtitle(option)}
                                </span>
                              )
                              : null}
                          </span>
                        </>
                      )}

                    {selected
                      ? <Check className="ml-auto size-4 shrink-0 text-primary" />
                      : null}
                  </li>
                );
              })}
            </ul>

            {hasFooter
              ? (
                <>
                  <div className="h-px bg-hairline" />

                  <div
                    className={cn(
                      'flex items-center gap-2 bg-primary/10 px-3 py-2.5',
                      'text-[12.5px] font-medium text-primary-on-dark',
                    )}
                  >
                    {footerNode}
                  </div>
                </>
              )
              : null}
          </div>
        )
        : null}
    </div>
  );
});

Autocomplete.displayName = 'Autocomplete';

export { Autocomplete };
export default Autocomplete;
